import React, { useState, useEffect } from 'react';
import { Box, Typography, Container, List, ListItem, ListItemButton, ListItemText, Paper, Divider, CircularProgress, Chip, Button, Tabs, Tab, TextField, InputAdornment, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Alert, Autocomplete } from '@mui/material';
import { Description, Folder, Search, Clear, Share as ShareIcon, ContentCopy as CopyIcon } from '@mui/icons-material';
import axios from 'axios';
import { API_BASE } from '../config';
import FastDocumentViewer from './FastDocumentViewer';

function Documentation() {
  const [documents, setDocuments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sidebarSearchQuery, setSidebarSearchQuery] = useState('');
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareMessage, setShareMessage] = useState('');
  const [loadingDocumentId, setLoadingDocumentId] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch ONLY documentation categories and documents (not swagger)
        const categoriesResponse = await axios.get(`${API_BASE}/documentation-categories`);
        const documentsResponse = await axios.get(`${API_BASE}/documentation-documents`);
        
        setDocuments(documentsResponse.data || []);
        setCategories(categoriesResponse.data || []);
        
        // Check if there's a shared document to open via URL parameter or sessionStorage
        const urlParams = new URLSearchParams(window.location.search);
        let shareCode = urlParams.get('doc');
        
        console.log('Documentation: Checking for shareCode in URL:', shareCode);
        
        // If not in URL, check sessionStorage (after login redirect)
        if (!shareCode) {
          shareCode = sessionStorage.getItem('openDocWithShareCode');
          console.log('Documentation: Checking sessionStorage for shareCode:', shareCode);
          // DON'T remove immediately - let it persist for React strict mode double-run
          // We'll remove it after a delay below
        }
        
        if (shareCode) {
          console.log('Documentation: Found shareCode, searching for document:', shareCode);
          // Find document by share code
          const sharedDoc = (documentsResponse.data || []).find(doc => doc.shareCode === shareCode);
          if (sharedDoc) {
            console.log('Documentation: Found shared document:', sharedDoc.title);
            setSelectedCategory(sharedDoc.category);
            setSelectedDocument(sharedDoc);
            
            // Remove from sessionStorage after a delay to handle React strict mode
            setTimeout(() => {
              sessionStorage.removeItem('openDocWithShareCode');
              console.log('Documentation: Removed shareCode from sessionStorage');
            }, 1000);
            
            // Remove the parameter from URL without reload
            if (urlParams.get('doc')) {
              window.history.replaceState({}, '', window.location.pathname);
            }
            return;
          } else {
            console.warn('Documentation: No document found with shareCode:', shareCode);
          }
        } else {
          console.log('Documentation: No shareCode found, using default document');
        }
        
        // Auto-select first category and its first document (default behavior)
        if (categoriesResponse.data?.length > 0) {
          setSelectedCategory(categoriesResponse.data[0].id);
          const firstCategoryDocs = (documentsResponse.data || []).filter(
            doc => doc.category === categoriesResponse.data[0].id
          );
          if (firstCategoryDocs.length > 0) {
            setSelectedDocument(firstCategoryDocs[0]);
          }
        }
        
        // Retry check for sessionStorage after a delay (handles timing issues)
        // This is needed when URL params are cleared before Documentation reads them
        if (!shareCode) {
          setTimeout(() => {
            const delayedShareCode = sessionStorage.getItem('openDocWithShareCode');
            if (delayedShareCode) {
              console.log('Documentation: Found shareCode on retry:', delayedShareCode);
              sessionStorage.removeItem('openDocWithShareCode');
              
              const sharedDoc = (documentsResponse.data || []).find(doc => doc.shareCode === delayedShareCode);
              if (sharedDoc) {
                console.log('Documentation: Opening document after retry:', sharedDoc.title);
                setSelectedCategory(sharedDoc.category);
                setSelectedDocument(sharedDoc);
              }
            }
          }, 200); // Check again after 200ms
        }
      } catch (error) {
        console.error('Error fetching documents:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Create PDF blob URL when selected document changes
  useEffect(() => {
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }

    if (selectedDocument?.filePath) {
      // If document has a file path, we don't need to create a blob URL
      // The iframe will load it directly from the server
      return;
    }

    if (selectedDocument?.content && 
        typeof selectedDocument.content === 'string' && 
        selectedDocument.content.startsWith('%PDF')) {
      const blob = new Blob([selectedDocument.content], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
    }

    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [selectedDocument]);

  const handleCategoryClick = (categoryId) => {
    setSelectedCategory(categoryId);
    const categoryDocs = documents.filter(doc => doc.category === categoryId);
    if (categoryDocs.length > 0) {
      setSelectedDocument(categoryDocs[0]);
    } else {
      setSelectedDocument(null);
    }
  };

  const handleDocumentClick = (doc) => {
    setLoadingDocumentId(doc.id);
    setSelectedDocument(doc);
    // Switch to the document's category if different
    if (doc.category !== selectedCategory) {
      setSelectedCategory(doc.category);
    }
  };

  const handleDocumentLoadComplete = () => {
    setLoadingDocumentId(null);
  };

  const handleSidebarSearchChange = (event) => {
    setSidebarSearchQuery(event.target.value);
  };

  const handleClearSidebarSearch = () => {
    setSidebarSearchQuery('');
  };

  // Handle global search selection from autocomplete
  const handleGlobalSearchSelect = (event, value) => {
    if (value) {
      handleDocumentClick(value);
      setSearchQuery(''); // Clear search after selection
    }
  };

  const handleCopyShareUrl = () => {
    if (!selectedDocument?.shareCode) {
      alert('Share code not available for this document');
      return;
    }
    // Share URL opens the documentation tab with auto-select
    const shareUrl = `${window.location.origin}?tab=2&doc=${selectedDocument.shareCode}`;
    navigator.clipboard.writeText(shareUrl);
    setShareMessage('Share URL copied to clipboard!');
    setTimeout(() => setShareMessage(''), 3000);
  };

  const getShareUrl = () => {
    if (!selectedDocument?.shareCode) return '';
    return `${window.location.origin}?tab=2&doc=${selectedDocument.shareCode}`;
  };

  // Filter documents based on sidebar search query (category-specific)
  const getFilteredDocuments = () => {
    let docs = selectedCategory 
      ? documents.filter(doc => doc.category === selectedCategory) 
      : [];
    
    // Apply sidebar search filter (category-specific)
    if (sidebarSearchQuery.trim()) {
      const query = sidebarSearchQuery.toLowerCase();
      docs = docs.filter(doc => 
        doc.title?.toLowerCase().includes(query) || 
        doc.description?.toLowerCase().includes(query)
      );
    }
    
    return docs;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const filteredDocuments = getFilteredDocuments();

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Header Section - Modern Gradient */}
      <Paper 
        elevation={0} 
        sx={{ 
          background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 50%, #7e22ce 100%)',
          color: 'white',
          p: 2,
          mb: 3,
          borderRadius: 0,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: '-50%',
            right: '-10%',
            width: '500px',
            height: '500px',
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.1)',
            zIndex: 0
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: '-30%',
            left: '-5%',
            width: '400px',
            height: '400px',
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.08)',
            zIndex: 0
          }
        }}
      >
        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{
              width: 40,
              height: 40,
              borderRadius: '10px',
              background: 'rgba(255, 255, 255, 0.2)',
              backdropFilter: 'blur(10px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1.5rem',
              boxShadow: '0 4px 16px rgba(0,0,0,0.2)'
            }}>
              📚
            </Box>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                Documentation
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Explore comprehensive guides, API references, and technical resources
              </Typography>
            </Box>
          </Box>
        </Container>
      </Paper>

      <Container maxWidth="xl" sx={{ pb: 2 }}>
        {categories.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h6" sx={{ color: '#999', mb: 2 }}>
              📚 No documents available yet
            </Typography>
            <Typography variant="body2" sx={{ color: '#666' }}>
              Documents will appear here once they are added through the Admin panel
            </Typography>
          </Box>
        ) : (
          <>
            {/* Categories Tabs - Top */}
            <Paper sx={{ mb: 2 }}>
              <Tabs
                value={selectedCategory}
                onChange={(e, newValue) => handleCategoryClick(newValue)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.9rem',
                    minHeight: 48,
                  }
                }}
              >
                {categories.map((cat) => (
                  <Tab
                    key={cat.id}
                    value={cat.id}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Folder sx={{ fontSize: 18 }} />
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {cat.label}
                        </Typography>
                        <Chip
                          label={documents.filter(doc => doc.category === cat.id).length}
                          color={cat.color || 'default'}
                          size="small"
                          sx={{ height: 18, fontSize: '0.65rem' }}
                        />
                      </Box>
                    }
                  />
                ))}
              </Tabs>
            </Paper>
          </>
        )}
      </Container>

      {/* Content Area - Full Width & Height */}
      {categories.length > 0 && selectedDocument && (
        <Box sx={{ display: 'flex', width: '100%', flex: 1, overflow: 'hidden', flexDirection: 'column' }}>
          {/* Document Header with Share Button */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            p: 2,
            borderBottom: '1px solid #e0e0e0',
            bgcolor: '#f5f5f5',
            flexWrap: 'wrap',
            gap: 2
          }}>
            <Box sx={{ flex: 1, minWidth: 200 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {selectedDocument.title}
              </Typography>
              {selectedDocument.description && (
                <Typography variant="caption" sx={{ color: '#666' }}>
                  {selectedDocument.description}
                </Typography>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              {/* Global Search with Dropdown */}
              <Autocomplete
                freeSolo
                options={documents}
                getOptionLabel={(option) => option.title || ''}
                filterOptions={(options, { inputValue }) => {
                  if (!inputValue || inputValue.length < 3) return [];
                  const query = inputValue.toLowerCase();
                  return options.filter(doc => 
                    doc.title?.toLowerCase().includes(query) || 
                    doc.description?.toLowerCase().includes(query)
                  );
                }}
                onChange={handleGlobalSearchSelect}
                inputValue={searchQuery}
                onInputChange={(event, newValue) => setSearchQuery(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    placeholder="Search all documents..."
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start">
                            <Search sx={{ fontSize: 20, color: '#666' }} />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {option.title}
                        </Typography>
                        <Chip
                          label={categories.find(c => c.id === option.category)?.label || option.category}
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: '0.65rem',
                            bgcolor: categories.find(c => c.id === option.category)?.color === 'blue' ? '#e3f2fd' :
                                    categories.find(c => c.id === option.category)?.color === 'green' ? '#e8f5e9' :
                                    categories.find(c => c.id === option.category)?.color === 'orange' ? '#fff3e0' :
                                    '#f5f5f5',
                            color: categories.find(c => c.id === option.category)?.color === 'blue' ? '#1976d2' :
                                   categories.find(c => c.id === option.category)?.color === 'green' ? '#2e7d32' :
                                   categories.find(c => c.id === option.category)?.color === 'orange' ? '#ed6c02' :
                                   '#666',
                          }}
                        />
                      </Box>
                      {option.description && (
                        <Typography variant="caption" sx={{ color: '#666' }}>
                          {option.description.substring(0, 60)}{option.description.length > 60 ? '...' : ''}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}
                sx={{ minWidth: 350 }}
              />
              <Button
                variant="outlined"
                startIcon={<ShareIcon />}
                onClick={() => setShareDialogOpen(true)}
                size="small"
              >
                Share
              </Button>
            </Box>
          </Box>

          {/* Main Content Area with Sidebar - Side by Side */}
          <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden', position: 'relative' }}>
            {/* Loading Overlay - Shows immediately when document is clicked */}
            {loadingDocumentId && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  bgcolor: 'rgba(255, 255, 255, 0.9)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 1000,
                  gap: 2
                }}
              >
                <CircularProgress 
                  size={80} 
                  thickness={4}
                  sx={{ color: '#2e7d32' }}
                />
                <Typography variant="h6" sx={{ color: '#2e7d32', fontWeight: 600 }}>
                  Loading document...
                </Typography>
                <Typography variant="body2" sx={{ color: '#666' }}>
                  Please wait while we prepare your document
                </Typography>
              </Box>
            )}
            
            {/* Main Content - Fast Document Viewer */}
            <Box sx={{ flex: 1 }}>
            {selectedDocument.filePath ? (
              <FastDocumentViewer
                documentUrl={`${API_BASE.replace('/api','')}${selectedDocument.filePath}`}
                title={selectedDocument.title}
                onLoadComplete={handleDocumentLoadComplete}
              />
            ) : pdfUrl ? (
              <FastDocumentViewer
                documentUrl={pdfUrl}
                title={selectedDocument.title}
                onLoadComplete={handleDocumentLoadComplete}
              />
            ) : selectedDocument.content ? (
              <Box sx={{
                bgcolor: '#f9f9f9',
                p: 3,
                borderRadius: 2,
                border: '1px solid #e0e0e0',
                maxHeight: '100vh',
                overflow: 'auto'
              }}>
                <Typography
                  component="div"
                  sx={{
                    whiteSpace: 'pre-wrap',
                    fontFamily: 'monospace',
                    fontSize: '0.9rem',
                    lineHeight: 1.6
                  }}
                >
                  {selectedDocument.content}
                </Typography>
              </Box>
            ) : (
              <Box sx={{ p: 6, textAlign: 'center' }}>
                <Typography variant="body1" sx={{ color: '#999' }}>
                  No content available for this document
                </Typography>
              </Box>
            )}
          </Box>

          {/* Right Sidebar - Document List - Extended scrolling */}
          <Paper sx={{ 
            width: '250px', 
            borderRadius: 0, 
            borderLeft: '1px solid #e0e0e0',
            display: 'flex',
            flexDirection: 'column',
            maxHeight: 'calc(100vh - 100px)'
          }}>
            <Box sx={{ p: 1.5, bgcolor: '#2e7d32', color: 'white' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Description sx={{ fontSize: 18 }} /> Documents
              </Typography>
            </Box>
            
            {/* Category Search Bar */}
            <Box sx={{ p: 1.5, borderBottom: '1px solid #e0e0e0' }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search in this category..."
                value={sidebarSearchQuery}
                onChange={handleSidebarSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ fontSize: 20, color: '#666' }} />
                    </InputAdornment>
                  ),
                  endAdornment: sidebarSearchQuery && (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={handleClearSidebarSearch}
                        edge="end"
                      >
                        <Clear sx={{ fontSize: 18 }} />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: '#d0d0d0',
                    },
                    '&:hover fieldset': {
                      borderColor: '#2e7d32',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#2e7d32',
                    },
                  },
                }}
              />
            </Box>

            <List sx={{ p: 0, overflow: 'auto', flex: 1 }}>
              {filteredDocuments.length === 0 ? (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="body2" sx={{ color: '#999' }}>
                    {sidebarSearchQuery ? 'No documents match your search' : 'No documents available'}
                  </Typography>
                </Box>
              ) : (
                filteredDocuments.map((doc) => (
                  <React.Fragment key={doc.id}>
                    <ListItem disablePadding>
                      <ListItemButton
                        selected={selectedDocument?.id === doc.id}
                        onClick={() => handleDocumentClick(doc)}
                        disabled={loadingDocumentId === doc.id}
                        sx={{
                          py: 0.75,
                          px: 1.5,
                          '&.Mui-selected': {
                            bgcolor: '#e8f5e9',
                            borderLeft: '3px solid #2e7d32'
                          }
                        }}
                      >
                        <ListItemText
                          primary={
                            <Typography
                              sx={{ 
                                fontWeight: 600, 
                                fontSize: '0.75rem',
                                color: '#1a5f3e',
                                wordWrap: 'break-word',
                                whiteSpace: 'normal',
                                overflowWrap: 'break-word',
                                lineHeight: 1.3
                              }}
                            >
                              {doc.title}
                            </Typography>
                          }
                          secondary={doc.description?.substring(0, 30) + (doc.description?.length > 30 ? '...' : '')}
                          secondaryTypographyProps={{
                            sx: { 
                              fontSize: '0.65rem',
                              color: '#556b7d',
                              wordWrap: 'break-word',
                              whiteSpace: 'normal',
                              overflowWrap: 'break-word',
                              lineHeight: 1.2,
                              mt: 0.25
                            }
                          }}
                        />
                        {loadingDocumentId === doc.id && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <CircularProgress 
                              size={24} 
                              thickness={5}
                              sx={{ 
                                color: '#2e7d32'
                              }} 
                            />
                            <Typography variant="caption" sx={{ color: '#2e7d32', fontWeight: 600 }}>
                              Loading...
                            </Typography>
                          </Box>
                        )}
                      </ListItemButton>
                    </ListItem>
                    <Divider />
                  </React.Fragment>
                ))
              )}
            </List>
          </Paper>
          </Box>
        </Box>
      )}

      {/* Share Dialog */}
      <Dialog open={shareDialogOpen} onClose={() => setShareDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Share Document</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {shareMessage && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {shareMessage}
            </Alert>
          )}
          <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
            Share this document with others. Recipients must log in to view the document.
          </Typography>
          <Paper sx={{ p: 2, bgcolor: '#f9f9f9', border: '1px solid #e0e0e0' }}>
            <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 1 }}>
              Share URL:
            </Typography>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center',
              gap: 1,
              p: 1,
              bgcolor: 'white',
              border: '1px solid #d0d0d0',
              borderRadius: 1,
              fontFamily: 'monospace',
              fontSize: '0.85rem',
              wordBreak: 'break-all'
            }}>
              <Box sx={{ flex: 1, overflow: 'auto' }}>
                {getShareUrl()}
              </Box>
              <IconButton
                size="small"
                onClick={handleCopyShareUrl}
                title="Copy to clipboard"
              >
                <CopyIcon fontSize="small" />
              </IconButton>
            </Box>
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShareDialogOpen(false)}>Close</Button>
          <Button 
            variant="contained" 
            onClick={handleCopyShareUrl}
            startIcon={<CopyIcon />}
          >
            Copy Link
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Documentation;
